import { MINDMAP_ROOT_NODE_ID, MINDMAP_VERSION } from './MindmapConstants';
import type { Mindmap, MindmapNode } from './MindmapTypes';
import type { MindmapSyntaxNode, MindmapSyntaxTree } from '@/features/mindmap/core/syntax/Ast';
import { resolveNodeStyles } from '@/features/mindmap/core/syntax/Tokens';
import type { CascadingStyle } from '@/features/mindmap/core/syntax/Tokens';

const DEFAULT_CONTEXT: CascadingStyle = { shape: 'rounded' };

const mapDirection = (direction: MindmapSyntaxTree['meta']['direction']): Mindmap['layoutDirection'] => {
  switch (direction) {
    case 'left':
      return 'left';
    case 'right':
      return 'right';
    case 'both':
    default:
      return 'right';
  }
};

export function syntaxTreeToMindmap(tree: MindmapSyntaxTree): Mindmap {
  const nodes: MindmapNode[] = [];
  if (!tree.root) {
    return {
      id: 'mindmap-text-import',
      version: MINDMAP_VERSION,
      rootId: MINDMAP_ROOT_NODE_ID,
      nodes,
      layoutDirection: mapDirection(tree.meta.direction),
      meta: { directionPreference: tree.meta.direction },
    };
  }

  let counter = 0;
  let effectiveDirection = tree.meta.direction;

  const nextId = () => `mm_${++counter}`;

  const visit = (
    syntaxNode: MindmapSyntaxNode,
    parentId: string | null,
    context = DEFAULT_CONTEXT,
    order = 0
  ) => {
    const { applied, nextContext, direction } = resolveNodeStyles(syntaxNode.tokens, context);
    if (parentId == null && direction) {
      effectiveDirection = direction;
    }

    const id = parentId == null ? MINDMAP_ROOT_NODE_ID : nextId();
    const node: MindmapNode = {
      id,
      parentId,
      text: syntaxNode.title,
      order,
      textColor: applied.textColor,
      bgColor: applied.bgColor,
      borderColor: applied.borderColor,
      shape: applied.shape,
      link: syntaxNode.link,
      fontSize: applied.fontSize,
      lineHeight: applied.lineHeight,
      boxShadow: applied.boxShadow,
      borderStyle: applied.borderStyle,
      borderWidth: applied.borderWidth,
      borderRadius: applied.borderRadius,
      styleTokens: [...syntaxNode.tokens],
    };

    nodes.push(node);

    syntaxNode.children.forEach((child, index) => {
      visit(child, id, nextContext, index);
    });
  };

  visit(tree.root, null, DEFAULT_CONTEXT, 0);

  return {
    id: 'mindmap-text-import',
    version: MINDMAP_VERSION,
    rootId: MINDMAP_ROOT_NODE_ID,
    nodes,
    layoutDirection: mapDirection(effectiveDirection),
    meta: { directionPreference: effectiveDirection },
  };
}

const directionFromMindmap = (mindmap: Mindmap): MindmapSyntaxTree['meta']['direction'] => {
  const preferred = mindmap.meta?.directionPreference;
  if (preferred === 'left' || preferred === 'both') return preferred;
  if (mindmap.layoutDirection === 'left') return 'left';
  return 'right';
};

const buildChildrenMap = (nodes: MindmapNode[]) => {
  const map = new Map<string, MindmapNode[]>();
  for (const node of nodes) {
    const key = node.parentId ?? '__root__';
    const list = map.get(key);
    if (list) {
      list.push(node);
    } else {
      map.set(key, [node]);
    }
  }
  for (const [, list] of map) {
    list.sort((a, b) => a.order - b.order);
  }
  return map;
};

const toSyntaxNode = (
  node: MindmapNode,
  depth: number,
  childrenMap: Map<string, MindmapNode[]>
): MindmapSyntaxNode => {
  // Use the node's styleTokens directly (source of truth) instead of deriving from computed properties
  // This prevents cascaded/inherited styles from being permanently added to child nodes
  const tokens = (node.styleTokens ?? []).filter((token) => !token.startsWith('#dir-'));
  const syntaxNode: MindmapSyntaxNode = {
    title: node.text,
    link: node.link,
    tokens,
    children: [],
    depth,
    line: depth,
  };

  const children = childrenMap.get(node.id) ?? [];
  syntaxNode.children = children.map((child) => toSyntaxNode(child, depth + 1, childrenMap));

  return syntaxNode;
};

export function mindmapToSyntaxTree(mindmap: Mindmap): MindmapSyntaxTree {
  const nodesById = new Map<string, MindmapNode>();
  for (const node of mindmap.nodes) nodesById.set(node.id, node);
  const rootNode = nodesById.get(mindmap.rootId);

  if (!rootNode) {
    return { root: null, meta: { direction: 'right' }, errors: ['Missing root node'] };
  }

  const childrenMap = buildChildrenMap(mindmap.nodes);
  const root = toSyntaxNode(rootNode, 0, childrenMap);

  const direction = directionFromMindmap(mindmap);
  root.tokens = root.tokens.filter((token) => !token.startsWith('#dir-'));
  if (direction !== 'right') {
    root.tokens = [`#dir-${direction}`, ...root.tokens];
  }

  return { root, meta: { direction }, errors: [] };
}
